import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { FleetPage, FleetSection, Notice } from '../../../../shared/sso-workspace/fleet-page'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { ConfirmDialog } from '../../../../shared/sso-workspace/confirm-dialog'
import { formatAgo } from '../../../../shared/sso-workspace/format'
import { serverMessage } from '../../../../shared/error/server-message'
import { utcText } from '../../../../shared/date/local-time'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat } from '../../../../shared/table-builder/table-columns'
import { IconList } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useRoleSysPackages } from '../../../../role/hook/role-sys'
import { getDownloadURL, getVersion, setDeprecated } from './packages-api'
import { commitURL, formatBytes } from './format'
import { DeprecateDialog } from './deprecate-dialog'
import { DeleteVersionDialog } from './delete-version-dialog'
import { PackagesUnavailable, useAvailable } from './unavailable'

const ago = v => (v ? <span title={utcText(v)}>{formatAgo(v)}</span> : '—')

const Detail = ({ label, children }) =>
  <div className="d-flex gap-3 mb-1"><dt className="fw-normal text-muted" style={{ minWidth: 130 }}>{label}</dt><dd className="mb-0 text-break">{children}</dd></div>

const Publisher = ({ v }) => {
  if (v.importedFrom) return 'Imported from Nexus'
  const { repo, commit } = v.source || {}
  const href = commitURL(repo, commit)
  if (!href) return '—'
  return <a className="btn btn-sm sw-btn-outline" href={href} target="_blank" rel="noopener noreferrer">{repo}@{commit.slice(0, 7)}</a>
}

const DownloadsGrid = ({ days }) => {
  const tableRef = useRef()
  const getRows = useMemo(() => async (start, end) => ({ data: days.slice(start, end), count: days.length }), [days])
  return (
    <FleetGrid title="downloads by day" tableRef={tableRef} flush>
      <TableAgInfinite key={JSON.stringify(days)} ref={tableRef} queryKey="day" onGetRows={getRows}
        emptyState={{ Icon: IconList, title: 'No downloads', message: 'Nothing was downloaded in the last 90 days.' }}>
        <ColumnFormat field="day" header="Day" sortable={false} minWidth={140} flex={1} render={r => (r.data ? r.value : '')} />
        <ColumnFormat field="downloads" header="Downloads" sortable={false} minWidth={120} render={r => (r.data ? String(r.value || 0) : '')} />
      </TableAgInfinite>
    </FleetGrid>
  )
}

const VersionPageBody = () => {
  const { name, channel, version } = useParams()
  const role = useRoleSysPackages()
  const history = useHistory()
  const [detail, setDetail] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const [downloadError, downloadFailure, setDownloadError] = useErrorLine()
  const [undeprecateError, undeprecateFailure, setUndeprecateError] = useErrorLine()
  const [deprecating, setDeprecating] = useState(false)
  const [undeprecating, setUndeprecating] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    getVersion(name, channel, version).then(v => { setError(null); setDetail(v) },
      err => setError(serverMessage(err, "The version couldn't be loaded."), err))
  }, [name, channel, version, setError])
  useEffect(() => { setDetail(null); load() }, [load])

  // The package page keeps its channel and its "Show deleted" in the address.
  const backQuery = new URLSearchParams()
  if (channel === 'unity-dev') backQuery.set('channel', 'unity-dev')
  if (detail && detail.deletedAt) backQuery.set('deleted', '1')
  const back = `/admin/packages/${encodeURIComponent(name)}${backQuery.toString() ? `?${backQuery}` : ''}`
  // After a delete: the plain package page (its channel kept), not the deleted list.
  const plainBack = `/admin/packages/${encodeURIComponent(name)}${channel === 'unity-dev' ? '?channel=unity-dev' : ''}`
  const backLink = <Link className="btn sw-btn" to={back}>{name}</Link>

  if (error) {
    return <FleetPage title={version} actions={backLink}><Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice></FleetPage>
  }
  if (!detail) return <FleetPage title={version} actions={backLink} />

  const deleted = Boolean(detail.deletedAt)
  const download = async () => {
    setDownloadError(null)
    try {
      const answer = await getDownloadURL(name, channel, version)
      window.open(answer.url, '_blank', 'noopener')
    } catch (err) {
      setDownloadError(serverMessage(err, "The download link couldn't be made."), err)
    }
  }
  const undeprecate = async () => {
    if (busy) return
    setBusy(true)
    setUndeprecateError(null)
    try {
      await setDeprecated(name, channel, version, null)
      setUndeprecating(false)
      load()
    } catch (err) {
      setUndeprecateError(serverMessage(err, "The version couldn't be undeprecated."), err)
    }
    setBusy(false)
  }

  const actions = (
    <>
      {role.accessRead && !deleted && <button type="button" className="btn sw-btn" onClick={download}>Download</button>}
      {role.accessUpdate && !deleted && (detail.deprecated
        ? <button type="button" className="btn sw-btn-outline" onClick={() => { setUndeprecateError(null); setUndeprecating(true) }}>Undeprecate</button>
        : <button type="button" className="btn sw-btn-outline" onClick={() => setDeprecating(true)}>Deprecate</button>)}
      {role.accessDelete && !deleted && <button type="button" className="btn sw-btn-danger" onClick={() => setDeleting(true)}>Delete version</button>}
      {backLink}
    </>
  )

  return (
    <FleetPage title={`${name} ${version}`} purpose={channel === 'unity-dev' ? 'Pre-release (unity-dev)' : 'Released (unity)'} actions={actions}>
      {downloadError && <Notice tone="bad" role="alert">{downloadError}<ErrorReference error={downloadFailure} /></Notice>}
      {deleted && <Notice tone="warn">This version was deleted {formatAgo(detail.deletedAt)}. Its file stays recoverable for 30 days.</Notice>}
      {detail.deprecated && <Notice tone="warn">Deprecated: {detail.deprecated}</Notice>}
      <FleetSection id="details" title="Details">
        <dl className="mb-0">
          <Detail label="Size">{formatBytes(detail.sizeBytes)}</Detail>
          <Detail label="Published">{ago(detail.publishedAt)}</Detail>
          <Detail label="Publisher"><Publisher v={detail} /></Detail>
          <Detail label="Signed">{detail.signed ? 'Yes' : 'No'}</Detail>
          <Detail label="Downloads">{String(detail.downloads || 0)}</Detail>
          <Detail label="SHA-1"><span className="sw-mono">{detail.sha1 || '—'}</span></Detail>
          <Detail label="Integrity"><span className="sw-mono">{detail.integrity || '—'}</span></Detail>
          {detail.importedFrom && <Detail label="Imported from">{detail.importedFrom}</Detail>}
          {deleted && <Detail label="Deleted">{ago(detail.deletedAt)}</Detail>}
          {detail.deprecated && <Detail label="Status"><Pill tone="warn">Deprecated</Pill></Detail>}
        </dl>
      </FleetSection>
      <FleetSection id="manifest" title="package.json">
        <pre className="sw-mono mb-0" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(detail.manifest, null, 2)}</pre>
      </FleetSection>
      <FleetSection id="changelog" title="Changelog">
        {detail.changelog
          ? <pre className="sw-mono mb-0" style={{ whiteSpace: 'pre-wrap' }}>{detail.changelog}</pre>
          : <p className="sw-hint mb-0">No changelog in this version</p>}
      </FleetSection>
      <FleetSection id="downloads" title="Downloads by day" lede="The last 90 days.">
        <DownloadsGrid days={detail.downloadsByDay || []} />
      </FleetSection>
      <DeprecateDialog open={deprecating} packageName={name} channel={channel} version={version}
        onCancel={() => setDeprecating(false)} onDone={() => { setDeprecating(false); load() }} />
      <ConfirmDialog open={undeprecating} title={`Undeprecate ${version}?`} body="The deprecation message is removed and installers stop seeing it."
        confirmText="Undeprecate" confirmClassName="btn sw-btn-primary" busy={busy} error={undeprecateError} failure={undeprecateFailure}
        onConfirm={undeprecate} onCancel={() => { setUndeprecating(false); setUndeprecateError(null) }} />
      <DeleteVersionDialog open={deleting} packageName={name} channel={channel} version={version} downloads={detail.downloads}
        onCancel={() => setDeleting(false)} onDeleted={() => { setDeleting(false); history.push(plainBack) }} />
    </FleetPage>
  )
}

export const VersionPage = () => (useAvailable() ? <VersionPageBody /> : <PackagesUnavailable />)
